import React, { useState } from "react";
import { auth, db } from "../firebase";
import { createUserWithEmailAndPassword, updateProfile, sendEmailVerification } from "firebase/auth";
import { doc, setDoc } from "firebase/firestore";
import { useNavigate } from "react-router-dom";
import {
  Typography,
  TextField,
  Button,
  Alert,
  Paper,
  Container,
  Grid2 as Grid,
  Dialog,
  DialogContent,
  Box,
  Slide,
  Snackbar,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  FormControlLabel,
  Checkbox
} from "@mui/material";
import { Link } from "react-router-dom";
import ReactCountryFlag from "react-country-flag";
import MathGEMLogo from "../assets/MATHSGEM.png";
import ExitToAppIcon from '@mui/icons-material/ExitToApp';
import { styled } from '@mui/system';

// Animation for Dialog entrance
const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

// Styled modern Dialog components
const ModernDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    borderRadius: 16,
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
    background: 'linear-gradient(135deg, #ffffff 0%, #f0f4f8 100%)',
    border: '1px solid rgba(173, 223, 173, 0.3)',
    overflow: 'hidden',
    width: '100%',
    maxWidth: 400,
  },
}));

const ModernDialogContent = styled(DialogContent)(({ theme }) => ({
  padding: theme.spacing(4),
  textAlign: 'center',
}));

const ModernButton = styled(Button)(({ theme }) => ({
  borderRadius: 25,
  padding: theme.spacing(1, 4),
  background: 'linear-gradient(45deg, #4caf50 30%, #81c784 90%)',
  color: 'white',
  textTransform: 'none',
  fontWeight: 600,
  '&:hover': {
    background: 'linear-gradient(45deg, #45a049 30%, #75b377 90%)',
    transform: 'translateY(-2px)',
    boxShadow: '0 4px 12px rgba(76, 175, 80, 0.3)',
  },
  transition: 'all 0.3s ease-in-out',
}));

// Comprehensive list of country codes with ISO codes for flags
const countryCodes = [
  { code: "+93", name: "Afghanistan", iso: "AF" },
  { code: "+355", name: "Albania", iso: "AL" },
  { code: "+213", name: "Algeria", iso: "DZ" },
  { code: "+376", name: "Andorra", iso: "AD" },
  { code: "+244", name: "Angola", iso: "AO" },
  { code: "+1-268", name: "Antigua and Barbuda", iso: "AG" },
  { code: "+54", name: "Argentina", iso: "AR" },
  { code: "+374", name: "Armenia", iso: "AM" },
  { code: "+61", name: "Australia", iso: "AU" },
  { code: "+43", name: "Austria", iso: "AT" },
  { code: "+994", name: "Azerbaijan", iso: "AZ" },
  { code: "+1-242", name: "Bahamas", iso: "BS" },
  { code: "+973", name: "Bahrain", iso: "BH" },
  { code: "+880", name: "Bangladesh", iso: "BD" },
  { code: "+1-246", name: "Barbados", iso: "BB" },
  { code: "+375", name: "Belarus", iso: "BY" },
  { code: "+32", name: "Belgium", iso: "BE" },
  { code: "+501", name: "Belize", iso: "BZ" },
  { code: "+229", name: "Benin", iso: "BJ" },
  { code: "+975", name: "Bhutan", iso: "BT" },
  { code: "+591", name: "Bolivia", iso: "BO" },
  { code: "+387", name: "Bosnia and Herzegovina", iso: "BA" },
  { code: "+267", name: "Botswana", iso: "BW" },
  { code: "+55", name: "Brazil", iso: "BR" },
  { code: "+673", name: "Brunei", iso: "BN" },
  { code: "+359", name: "Bulgaria", iso: "BG" },
  { code: "+226", name: "Burkina Faso", iso: "BF" },
  { code: "+257", name: "Burundi", iso: "BI" },
  { code: "+855", name: "Cambodia", iso: "KH" },
  { code: "+237", name: "Cameroon", iso: "CM" },
  { code: "+1", name: "Canada", iso: "CA" },
  { code: "+238", name: "Cape Verde", iso: "CV" },
  { code: "+236", name: "Central African Republic", iso: "CF" },
  { code: "+235", name: "Chad", iso: "TD" },
  { code: "+56", name: "Chile", iso: "CL" },
  { code: "+86", name: "China", iso: "CN" },
  { code: "+57", name: "Colombia", iso: "CO" },
  { code: "+269", name: "Comoros", iso: "KM" },
  { code: "+243", name: "Congo, Democratic Republic of the", iso: "CD" },
  { code: "+242", name: "Congo, Republic of the", iso: "CG" },
  { code: "+506", name: "Costa Rica", iso: "CR" },
  { code: "+225", name: "Côte d'Ivoire", iso: "CI" },
  { code: "+385", name: "Croatia", iso: "HR" },
  { code: "+53", name: "Cuba", iso: "CU" },
  { code: "+357", name: "Cyprus", iso: "CY" },
  { code: "+420", name: "Czech Republic", iso: "CZ" },
  { code: "+45", name: "Denmark", iso: "DK" },
  { code: "+253", name: "Djibouti", iso: "DJ" },
  { code: "+1-767", name: "Dominica", iso: "DM" },
  { code: "+1-809", name: "Dominican Republic", iso: "DO" },
  { code: "+593", name: "Ecuador", iso: "EC" },
  { code: "+20", name: "Egypt", iso: "EG" },
  { code: "+503", name: "El Salvador", iso: "SV" },
  { code: "+240", name: "Equatorial Guinea", iso: "GQ" },
  { code: "+291", name: "Eritrea", iso: "ER" },
  { code: "+372", name: "Estonia", iso: "EE" },
  { code: "+268", name: "Eswatini", iso: "SZ" },
  { code: "+251", name: "Ethiopia", iso: "ET" },
  { code: "+679", name: "Fiji", iso: "FJ" },
  { code: "+358", name: "Finland", iso: "FI" },
  { code: "+33", name: "France", iso: "FR" },
  { code: "+241", name: "Gabon", iso: "GA" },
  { code: "+220", name: "Gambia", iso: "GM" },
  { code: "+995", name: "Georgia", iso: "GE" },
  { code: "+49", name: "Germany", iso: "DE" },
  { code: "+233", name: "Ghana", iso: "GH" },
  { code: "+30", name: "Greece", iso: "GR" },
  { code: "+1-473", name: "Grenada", iso: "GD" },
  { code: "+502", name: "Guatemala", iso: "GT" },
  { code: "+224", name: "Guinea", iso: "GN" },
  { code: "+245", name: "Guinea-Bissau", iso: "GW" },
  { code: "+592", name: "Guyana", iso: "GY" },
  { code: "+509", name: "Haiti", iso: "HT" },
  { code: "+504", name: "Honduras", iso: "HN" },
  { code: "+852", name: "Hong Kong", iso: "HK" },
  { code: "+36", name: "Hungary", iso: "HU" },
  { code: "+354", name: "Iceland", iso: "IS" },
  { code: "+91", name: "India", iso: "IN" },
  { code: "+62", name: "Indonesia", iso: "ID" },
  { code: "+98", name: "Iran", iso: "IR" },
  { code: "+964", name: "Iraq", iso: "IQ" },
  { code: "+353", name: "Ireland", iso: "IE" },
  { code: "+972", name: "Israel", iso: "IL" },
  { code: "+39", name: "Italy", iso: "IT" },
  { code: "+1-876", name: "Jamaica", iso: "JM" },
  { code: "+81", name: "Japan", iso: "JP" },
  { code: "+962", name: "Jordan", iso: "JO" },
  { code: "+7", name: "Kazakhstan", iso: "KZ" },
  { code: "+254", name: "Kenya", iso: "KE" },
  { code: "+686", name: "Kiribati", iso: "KI" },
  { code: "+383", name: "Kosovo", iso: "XK" },
  { code: "+965", name: "Kuwait", iso: "KW" },
  { code: "+996", name: "Kyrgyzstan", iso: "KG" },
  { code: "+856", name: "LaosCotton", iso: "LA" },
  { code: "+371", name: "Latvia", iso: "LV" },
  { code: "+961", name: "Lebanon", iso: "LB" },
  { code: "+266", name: "Lesotho", iso: "LS" },
  { code: "+231", name: "Liberia", iso: "LR" },
  { code: "+218", name: "Libya", iso: "LY" },
  { code: "+423", name: "Liechtenstein", iso: "LI" },
  { code: "+370", name: "Lithuania", iso: "LT" },
  { code: "+352", name: "Luxembourg", iso: "LU" },
  { code: "+853", name: "Macau", iso: "MO" },
  { code: "+389", name: "North Macedonia", iso: "MK" },
  { code: "+261", name: "Madagascar", iso: "MG" },
  { code: "+265", name: "Malawi", iso: "MW" },
  { code: "+60", name: "Malaysia", iso: "MY" },
  { code: "+960", name: "Maldives", iso: "MV" },
  { code: "+223", name: "Mali", iso: "ML" },
  { code: "+356", name: "Malta", iso: "MT" },
  { code: "+692", name: "Marshall Islands", iso: "MH" },
  { code: "+222", name: "Mauritania", iso: "MR" },
  { code: "+230", name: "Mauritius", iso: "MU" },
  { code: "+52", name: "Mexico", iso: "MX" },
  { code: "+691", name: "Micronesia", iso: "FM" },
  { code: "+373", name: "Moldova", iso: "MD" },
  { code: "+377", name: "Monaco", iso: "MC" },
  { code: "+976", name: "Mongolia", iso: "MN" },
  { code: "+382", name: "Montenegro", iso: "ME" },
  { code: "+212", name: "Morocco", iso: "MA" },
  { code: "+258", name: "Mozambique", iso: "MZ" },
  { code: "+95", name: "Myanmar", iso: "MM" },
  { code: "+264", name: "Namibia", iso: "NA" },
  { code: "+674", name: "Nauru", iso: "NR" },
  { code: "+977", name: "Nepal", iso: "NP" },
  { code: "+31", name: "Netherlands", iso: "NL" },
  { code: "+64", name: "New Zealand", iso: "NZ" },
  { code: "+505", name: "Nicaragua", iso: "NI" },
  { code: "+227", name: "Niger", iso: "NE" },
  { code: "+234", name: "Nigeria", iso: "NG" },
  { code: "+47", name: "Norway", iso: "NO" },
  { code: "+968", name: "Oman", iso: "OM" },
  { code: "+92", name: "Pakistan", iso: "PK" },
  { code: "+680", name: "Palau", iso: "PW" },
  { code: "+970", name: "Palestine", iso: "PS" },
  { code: "+507", name: "Panama", iso: "PA" },
  { code: "+675", name: "Papua New Guinea", iso: "PG" },
  { code: "+595", name: "Paraguay", iso: "PY" },
  { code: "+51", name: "Peru", iso: "PE" },
  { code: "+63", name: "Philippines", iso: "PH" },
  { code: "+48", name: "Poland", iso: "PL" },
  { code: "+351", name: "Portugal", iso: "PT" },
  { code: "+974", name: "Qatar", iso: "QA" },
  { code: "+40", name: "Romania", iso: "RO" },
  { code: "+7", name: "Russia", iso: "RU" },
  { code: "+250", name: "Rwanda", iso: "RW" },
  { code: "+1-869", name: "Saint Kitts and Nevis", iso: "KN" },
  { code: "+1-758", name: "Saint Lucia", iso: "LC" },
  { code: "+1-784", name: "Saint Vincent and the Grenadines", iso: "VC" },
  { code: "+685", name: "Samoa", iso: "WS" },
  { code: "+378", name: "San Marino", iso: "SM" },
  { code: "+239", name: "Sao Tome and Principe", iso: "ST" },
  { code: "+966", name: "Saudi Arabia", iso: "SA" },
  { code: "+221", name: "Senegal", iso: "SN" },
  { code: "+381", name: "Serbia", iso: "RS" },
  { code: "+248", name: "Seychelles", iso: "SC" },
  { code: "+232", name: "Sierra Leone", iso: "SL" },
  { code: "+65", name: "Singapore", iso: "SG" },
  { code: "+421", name: "Slovakia", iso: "SK" },
  { code: "+386", name: "Slovenia", iso: "SI" },
  { code: "+677", name: "Solomon Islands", iso: "SB" },
  { code: "+252", name: "Somalia", iso: "SO" },
  { code: "+27", name: "South Africa", iso: "ZA" },
  { code: "+82", name: "South Korea", isc: "KR" },
  { code: "+211", name: "South Sudan", iso: "SS" },
  { code: "+34", name: "Spain", iso: "ES" },
  { code: "+94", name: "Sri Lanka", iso: "LK" },
  { code: "+249", name: "Sudan", iso: "SD" },
  { code: "+597", name: "Suriname", iso: "SR" },
  { code: "+46", name: "Sweden", iso: "SE" },
  { code: "+41", name: "Switzerland", iso: "CH" },
  { code: "+963", name: "Syria", iso: "SY" },
  { code: "+886", name: "Taiwan", iso: "TW" },
  { code: "+992", name: "Tajikistan", iso: "TJ" },
  { code: "+255", name: "Tanzania", iso: "TZ" },
  { code: "+66", name: "Thailand", iso: "TH" },
  { code: "+670", name: "Timor-Leste", iso: "TL" },
  { code: "+228", name: "Togo", iso: "TG" },
  { code: "+676", name: "Tonga", iso: "TO" },
  { code: "+1-868", name: "Trinidad and Tobago", iso: "TT" },
  { code: "+216", name: "Tunisia", iso: "TN" },
  { code: "+90", name: "Turkey", iso: "TR" },
  { code: "+993", name: "Turkmenistan", iso: "TM" },
  { code: "+688", name: "Tuvalu", iso: "TV" },
  { code: "+256", name: "Uganda", iso: "UG" },
  { code: "+380", name: "Ukraine", iso: "UA" },
  { code: "+971", name: "United Arab Emirates", iso: "AE" },
  { code: "+44", name: "United Kingdom", iso: "GB" },
  { code: "+1", name: "United States", iso: "US" },
  { code: "+598", name: "Uruguay", iso: "UY" },
  { code: "+998", name: "Uzbekistan", iso: "UZ" },
  { code: "+678", name: "Vanuatu", iso: "VU" },
  { code: "+58", name: "Venezuela", iso: "VE" },
  { code: "+84", name: "Vietnam", iso: "VN" },
  { code: "+967", name: "Yemen", iso: "YE" },
  { code: "+260", name: "Zambia", iso: "ZM" },
  { code: "+263", name: "Zimbabwe", iso: "ZW" },
].sort((a, b) => a.name.localeCompare(b.name));

const SignUp = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [fullName, setFullName] = useState("");
  const [gradeLevel, setGradeLevel] = useState("");
  const [mobileNumber, setMobileNumber] = useState("");
  const [countryCode, setCountryCode] = useState("+248");
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [openErrorSnackbar, setOpenErrorSnackbar] = useState(false);
  const [isSigningUp, setIsSigningUp] = useState(false);
  const [subscribeNewsletter, setSubscribeNewsletter] = useState(false);

  const navigate = useNavigate();

  const getFriendlyErrorMessage = (errorCode) => {
    const errorMap = {
      "auth/invalid-email": "The email address you entered is invalid. Please check and try again.",
      "auth/email-already-in-use": "An account with this email already exists. Please sign in or use a different email.",
      "auth/weak-password": "Your password is too weak. Please use at least 6 characters.",
      "auth/too-many-requests": "Too many attempts. Please try again later.",
      "auth/network-request-failed": "Network error. Please check your internet connection and try again.",
      "auth/operation-not-allowed": "Email/password accounts are not enabled. Please contact support.",
      default: "An unexpected error occurred. Please try again or contact support.",
    };
    return errorMap[errorCode] || errorMap.default;
  };

  const handleSignUp = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    setOpenDialog(false);
    setOpenErrorSnackbar(false);
    setIsSigningUp(true);

    if (mobileNumber && !/^\d{7,15}$/.test(mobileNumber)) {
      setError("Please enter a valid mobile number (7-15 digits).");
      setOpenErrorSnackbar(true);
      setIsSigningUp(false);
      return;
    }

    try {
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;

      await updateProfile(user, { displayName: fullName });

      await sendEmailVerification(user, {
        url: 'https://mathsgemseychelles.com/sign-in',
        handleCodeInApp: false,
      });

      const trialEndDate = new Date();
      trialEndDate.setDate(trialEndDate.getDate() + 7);
      const fullMobileNumber = mobileNumber ? `${countryCode}${mobileNumber}` : "";

      // 1. Save user data to main "users" collection
      await setDoc(doc(db, "users", user.uid), {
        fullName,
        email: user.email,
        gradeLevel,
        mobileNumber: fullMobileNumber,
        countryCode,
        emailVerified: false,
        status: "trial",
        trialEndDate: trialEndDate.toISOString(),
        createdAt: new Date().toISOString(),
        role: 'user',
        plan: 'monthly',
        newsletterSubscribed: subscribeNewsletter, // Track preference here too
      });

      // 2. If subscribed, save to "newsletters" collection
      if (subscribeNewsletter) {
        await setDoc(doc(db, "newsletters", user.uid), {
          uid: user.uid,
          email: user.email,
          fullName: fullName,
          subscribedAt: new Date().toISOString(),
          active: true
        });
      }

      setSuccess(
        "Thank you for registering! A verification email has been sent. " +
        "Please verify your email to continue."
      );
      setOpenDialog(true);

      await auth.signOut();
    } catch (err) {
      console.error("Sign-up error:", err.code);
      setError(getFriendlyErrorMessage(err.code));
      setOpenErrorSnackbar(true);
    } finally {
      setIsSigningUp(false);
    }
  };

  const handleDialogClose = () => {
    setOpenDialog(false);
    navigate('/sign-in');
  };

  const handleErrorSnackbarClose = (event, reason) => {
    if (reason === "clickaway") return;
    setOpenErrorSnackbar(false);
  };

  const handleMobileNumberChange = (e) => {
    const value = e.target.value;
    // Allow only digits
    if (value === "" || /^\d*$/.test(value)) {
      setMobileNumber(value);
    }
  };

  const AnimatedIcon = styled(ExitToAppIcon)(({ theme }) => ({
    marginLeft: theme.spacing(1),
    transition: 'transform 0.3s ease-in-out',
    '.MuiButton-root:hover &': {
      animation: 'bounce 0.5s infinite',
    },
    '@keyframes bounce': {
      '0%, 100%': { transform: 'translateY(0)' },
      '50%': { transform: 'translateY(-5px)' },
    },
  }));

  return (
    <Container style={{ marginTop: 100 }}>
      <Paper sx={{ borderRadius: 5, margin: { md: 8, xs: 3 } }} elevation={8}>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: 6 }}>
            <Paper
              sx={{
                maxWidth: 400,
                mx: "auto",
                p: 3,
                borderRadius: 2,
                my: 5,
              }}
              elevation={0}
            >
              <Typography variant="h4" sx={{ fontWeight: 800 }} align="center" gutterBottom>
                Register
              </Typography>
              <Typography variant="subtitle1" align="center" gutterBottom>
                Enter your details below to create a Math Gem account.
              </Typography>

              <Snackbar
                open={openErrorSnackbar}
                autoHideDuration={6000}
                onClose={handleErrorSnackbarClose}
                anchorOrigin={{ vertical: "top", horizontal: "center" }}
              >
                <Alert
                  onClose={handleErrorSnackbarClose}
                  severity="error"
                  sx={{ width: "100%" }}
                >
                  {error}
                </Alert>
              </Snackbar>

              <ModernDialog
                open={openDialog}
                onClose={handleDialogClose}
                TransitionComponent={Transition}
                aria-labelledby="modern-dialog-title"
              >
                <ModernDialogContent>
                  <Typography
                    variant="h5"
                    sx={{
                      fontWeight: 700,
                      color: '#2e7d32',
                      mb: 2
                    }}
                  >
                    Welcome to Math Gem!
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{
                      color: '#424242',
                      mb: 3,
                      lineHeight: 1.6
                    }}
                  >
                    {success}
                  </Typography>
                  <ModernButton
                    onClick={handleDialogClose}
                  >
                    Got It!
                  </ModernButton>
                </ModernDialogContent>
              </ModernDialog>

              <form onSubmit={handleSignUp}>
                <TextField
                  label="Full Name"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  fullWidth
                  margin="normal"
                  required
                />
                <TextField
                  label="Email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  fullWidth
                  margin="normal"
                  required
                />
                <TextField
                  label="Password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  fullWidth
                  margin="normal"
                  required
                />
                <TextField
                  label="Grade Level"
                  value={gradeLevel}
                  onChange={(e) => setGradeLevel(e.target.value)}
                  fullWidth
                  margin="normal"
                  placeholder="E.g., Secondary 1 or Primary 5"
                  required
                />
                <Grid container spacing={1} alignItems="flex-end">
                  <Grid size={{ xs: 5 }}>
                    <FormControl fullWidth margin="normal">
                      <InputLabel id="country-code-label">Country Code</InputLabel>
                      <Select
                        labelId="country-code-label"
                        value={countryCode}
                        onChange={(e) => setCountryCode(e.target.value)}
                        label="Country Code"
                        renderValue={(selected) => {
                          const country = countryCodes.find(c => c.code === selected);
                          return country ? (
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <ReactCountryFlag
                                countryCode={country.iso}
                                svg
                                style={{ width: '1.5em', height: '1.5em', marginRight: '8px' }}
                              />
                              {country.code}
                            </Box>
                          ) : selected;
                        }}
                      >
                        {countryCodes.map((country) => (
                          <MenuItem key={country.code} value={country.code}>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <ReactCountryFlag
                                countryCode={country.iso}
                                svg
                                style={{ width: '1.5em', height: '1.5em', marginRight: '8px' }}
                              />
                              <Typography>{`${country.name} (${country.code})`}</Typography>
                            </Box>
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid size={{ xs: 7 }}>
                    <TextField
                      label="Mobile Number"
                      value={mobileNumber}
                      onChange={handleMobileNumberChange}
                      fullWidth
                      margin="normal"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">{countryCode}</InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                </Grid>
                <Grid container sx={{ mt: 1 }}>
                  <Grid size={{ xs: 12 }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={subscribeNewsletter}
                          onChange={(e) => setSubscribeNewsletter(e.target.checked)}
                          sx={{
                            color: '#2e7d32',
                            '&.Mui-checked': {
                              color: '#4caf50',
                            },
                          }}
                        />
                      }
                      label={
                        <Typography variant="body2" sx={{ color: '#555' }}>
                          I want to receive news, updates, and math tips via email.
                        </Typography>
                      }
                    />
                  </Grid>
                </Grid>
                <Grid
                  container
                  justifyContent="center"
                  sx={{ mt: 2 }}
                >
                  <Grid>
                    <Button
                      type="submit"
                      variant="outlined"
                      size="medium"
                      sx={{
                        borderColor: 'green',
                        color: 'green',
                        '&:hover': {
                          borderColor: 'green',
                          backgroundColor: 'rgba(255, 255, 255, 0.1)',
                        },
                        display: 'flex',
                        alignItems: 'center',
                        minWidth: 120,
                      }}
                      disabled={isSigningUp}
                    >
                      {isSigningUp ? (
                        <CircularProgress size={24} color="inherit" />
                      ) : (
                        <>
                          Register
                          <AnimatedIcon />
                        </>
                      )}
                    </Button>
                  </Grid>
                </Grid>

                <Typography variant="body2" align="center" sx={{ mt: 2 }}>
                  Already have an account?{" "}
                  <Link to="/sign-in" style={{ color: "blue", textDecoration: "none" }}>
                    <b>Sign In</b>
                  </Link>
                </Typography>
              </form>
            </Paper>
          </Grid>
          <Grid
            sx={{
              paddingTop: 10,
              backgroundColor: "#addfad",
              backgroundImage: "linear-gradient(480deg, #addfad 0%, #fffafa 74%)",
              borderRadius: "0 10px 10px 0px",
              display: { xs: 'none', md: 'block' }
            }}
            size={{ md: 6, xs: 12 }}
          >
            <Box sx={{ textAlign: "center" }}>
              <img style={{ width: 500 }} src={MathGEMLogo} alt="Math Gem Seychelles" />
            </Box>
          </Grid>
        </Grid>
      </Paper>
      <br />
      <br />
      <br />
      <br />
    </Container>
  );
};

export default SignUp;